import { DirectionSelect, parseDirection, parseDirectionFilter } from '@/components/travel/direction';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Search,
  CalendarCheck,
  ChevronLeft,
  ChevronRight,
  Loader2,
  User,
  Calendar,
  Users,
  Building2,
  Package,
  Plane,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { BookingModal } from '@/components/travel/bookings/booking-modal';
import { usePermission } from '@/components/guards/permission-guard';
import { useSessionState } from '@/hooks/use-session-state';

// Package booking statuses
const packageStatusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  CONFIRMED: 'bg-blue-100 text-blue-700',
  PARTIALLY_PAID: 'bg-orange-100 text-orange-700',
  FULLY_PAID: 'bg-green-100 text-green-700',
  TICKETED: 'bg-purple-100 text-purple-700',
  IN_PROGRESS: 'bg-cyan-100 text-cyan-700',
  COMPLETED: 'bg-gray-100 text-gray-700',
  CANCELLED: 'bg-red-100 text-red-700',
  REFUNDED: 'bg-pink-100 text-pink-700',
  PARTIALLY_REFUNDED: 'bg-pink-50 text-pink-600',
};

const packageStatusLabelKeys: Record<string, string> = {
  PENDING: 'status.pending',
  CONFIRMED: 'status.confirmed',
  PARTIALLY_PAID: 'status.partiallyPaid',
  FULLY_PAID: 'status.fullyPaid',
  TICKETED: 'status.ticketed',
  IN_PROGRESS: 'status.inProgress',
  COMPLETED: 'status.completed',
  CANCELLED: 'status.cancelled',
  REFUNDED: 'status.refunded',
  PARTIALLY_REFUNDED: 'status.partiallyRefunded',
};

// Hotel booking statuses
const hotelStatusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  CONFIRMED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
  REFUNDED: 'bg-pink-100 text-pink-700',
};

const hotelStatusLabelKeys: Record<string, string> = {
  PENDING: 'status.pending',
  CONFIRMED: 'status.confirmed',
  CANCELLED: 'status.cancelled',
  REFUNDED: 'status.refunded',
};

// Flight booking statuses
const flightStatusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  CONFIRMED: 'bg-blue-100 text-blue-700',
  TICKETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-red-100 text-red-700',
  REFUNDED: 'bg-pink-100 text-pink-700',
};

const flightStatusLabelKeys: Record<string, string> = {
  PENDING: 'status.pending',
  CONFIRMED: 'status.confirmed',
  TICKETED: 'status.ticketed',
  CANCELLED: 'status.cancelled',
  REFUNDED: 'status.refunded',
};

type PackageBookingStatus =
  | 'PENDING'
  | 'CONFIRMED'
  | 'PARTIALLY_PAID'
  | 'FULLY_PAID'
  | 'TICKETED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'REFUNDED';

type HotelBookingStatus = 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'REFUNDED';

type FlightBookingStatus = 'PENDING' | 'CONFIRMED' | 'TICKETED' | 'CANCELLED' | 'REFUNDED';

export function BookingsPage() {
  const { t, i18n } = useTranslation('bookings');
  const { t: tCommon } = useTranslation('common');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'packages';
  const direction = parseDirectionFilter(searchParams.get('direction'));

  // RBAC permission checks
  const canCreate = usePermission('BOOKINGS', 'CREATE');
  // Edit/delete available for future actions on list page
  const _canEdit = usePermission('BOOKINGS', 'EDIT');
  const _canDelete = usePermission('BOOKINGS', 'DELETE');
  void _canEdit;
  void _canDelete;

  // Package bookings state
  const [pkgDebouncedSearch, setPkgDebouncedSearch] = useSessionState(
    'travel:bookings:pkgDebouncedSearch',
    ''
  );
  const [pkgSearchQuery, setPkgSearchQuery] = useState(pkgDebouncedSearch);
  const [pkgPage, setPkgPage] = useState(1);
  const [pkgStatusFilter, setPkgStatusFilter] = useSessionState<PackageBookingStatus | undefined>(
    'travel:bookings:pkgStatusFilter',
    undefined
  );
  const [showBookingModal, setShowBookingModal] = useState(false);

  // Hotel bookings state
  const [hotelPage, setHotelPage] = useState(1);
  const [hotelStatusFilter, setHotelStatusFilter] = useSessionState<HotelBookingStatus | undefined>(
    'travel:bookings:hotelStatusFilter',
    undefined
  );

  // Flight bookings state
  const [flightPage, setFlightPage] = useState(1);
  const [flightStatusFilter, setFlightStatusFilter] = useSessionState<
    FlightBookingStatus | undefined
  >('travel:bookings:flightStatusFilter', undefined);

  const limit = 10;
  useEffect(() => { setPkgPage(1); setHotelPage(1); setFlightPage(1); }, [direction]);

  const handleTabChange = (tab: string) => {
    setSearchParams(previous => { const next = new URLSearchParams(previous); next.set('tab', tab); return next; });
  };

  const handlePkgSearchChange = (value: string) => {
    setPkgSearchQuery(value);
    setTimeout(() => {
      setPkgDebouncedSearch(value);
      setPkgPage(1);
    }, 300);
  };

  // Package bookings query
  const {
    data: pkgData,
    isLoading: pkgLoading,
    error: pkgError,
  } = trpc.booking.list.useQuery({
    page: pkgPage,
    limit,
    search: pkgDebouncedSearch || undefined,
    status: pkgStatusFilter,
    direction,
  });

  // Hotel bookings query
  const {
    data: hotelData,
    isLoading: hotelLoading,
    error: hotelError,
  } = trpc.hotel.listHotelBookings.useQuery({
    page: hotelPage,
    limit,
    status: hotelStatusFilter,
    direction,
  });

  // Flight bookings query
  const {
    data: flightData,
    isLoading: flightLoading,
    error: flightError,
  } = trpc.flight.listFlightBookings.useQuery({
    page: flightPage,
    limit,
    status: flightStatusFilter,
    direction,
  });

  const pkgBookings = pkgData?.bookings ?? [];
  const pkgPagination = pkgData?.pagination ?? { page: 1, limit: 10, total: 0, totalPages: 0 };

  const hotelBookings = hotelData?.hotelBookings ?? [];
  const hotelPagination = hotelData?.pagination ?? { page: 1, limit: 10, total: 0, totalPages: 0 };

  const flightBookings = flightData?.flightBookings ?? [];
  const flightPagination = flightData?.pagination ?? {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  };

  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleDateString(dateLocale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('title')}</h1>
          <p className="text-muted-foreground">{t('subtitle')}</p>
        </div>
        {activeTab === 'packages' && canCreate && (
          <Button onClick={() => navigate('/travel/bookings/new' + (direction ? '?direction=' + direction : ''))}>
            <Plus className="mr-2 h-4 w-4" />
            {t('newBooking')}
          </Button>
        )}
      </div>

      <DirectionSelect filter value={direction} onChange={value => setSearchParams(previous => { const next = new URLSearchParams(previous); if(value) next.set('direction', value); else next.delete('direction'); return next; })} />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="packages" className="gap-2">
            <Package className="h-4 w-4" />
            {t('tabs.packages')}
            {pkgPagination.total > 0 && (
              <span className="bg-primary/10 ml-1 px-2 py-0.5 text-xs">{pkgPagination.total}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="hotels" className="gap-2">
            <Building2 className="h-4 w-4" />
            {t('tabs.hotels')}
            {hotelPagination.total > 0 && (
              <span className="bg-primary/10 ml-1 px-2 py-0.5 text-xs">
                {hotelPagination.total}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="flights" className="gap-2">
            <Plane className="h-4 w-4" />
            {t('tabs.flights')}
            {flightPagination.total > 0 && (
              <span className="bg-primary/10 ml-1 px-2 py-0.5 text-xs">
                {flightPagination.total}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Package Bookings Tab */}
        <TabsContent value="packages" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative max-w-sm flex-1">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={pkgSearchQuery}
                onChange={(e) => handlePkgSearchChange(e.target.value)}
                className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full border px-2.5 py-1.5 pl-10 text-sm focus:outline-none focus:ring-1"
              />
            </div>
            <select
              value={pkgStatusFilter ?? ''}
              onChange={(e) => {
                setPkgStatusFilter((e.target.value as PackageBookingStatus) || undefined);
                setPkgPage(1);
              }}
              className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1"
            >
              <option value="">{t('allStatuses')}</option>
              <option value="PENDING">{t('status.pending')}</option>
              <option value="CONFIRMED">{t('status.confirmed')}</option>
              <option value="PARTIALLY_PAID">{t('status.partiallyPaid')}</option>
              <option value="FULLY_PAID">{t('status.fullyPaid')}</option>
              <option value="TICKETED">{t('status.ticketed')}</option>
              <option value="IN_PROGRESS">{t('status.inProgress')}</option>
              <option value="COMPLETED">{t('status.completed')}</option>
              <option value="CANCELLED">{t('status.cancelled')}</option>
              <option value="REFUNDED">{t('status.refunded')}</option>
            </select>
          </div>

          {/* Loading State */}
          {pkgLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="text-primary h-8 w-8 animate-spin" />
              <span className="text-muted-foreground ml-2">{t('loadingBookings')}</span>
            </div>
          )}

          {/* Error State */}
          {pkgError && (
            <div className="border border-red-200 bg-red-50 p-4 text-red-700">
              <p className="font-medium">{t('errorLoading')}</p>
              <p className="text-sm">{pkgError.message}</p>
            </div>
          )}

          {/* Empty State */}
          {!pkgLoading && !pkgError && pkgBookings.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <CalendarCheck className="text-muted-foreground/50 h-12 w-12" />
              <h3 className="mt-4 text-lg font-medium">{t('noBookingsFound')}</h3>
              <p className="text-muted-foreground mt-1 text-sm">
                {pkgDebouncedSearch || pkgStatusFilter
                  ? t('adjustFilters')
                  : t('bookingsWillAppear')}
              </p>
              {!pkgDebouncedSearch && !pkgStatusFilter && canCreate && (
                <Button className="mt-4" onClick={() => setShowBookingModal(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('newBooking')}
                </Button>
              )}
            </div>
          )}

          {/* Package Bookings Table */}
          {!pkgLoading && !pkgError && pkgBookings.length > 0 && (
            <>
              <div className="border-border bg-card overflow-hidden border">
                <table className="w-full">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('reference')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('customer')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('package')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('travelDates')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('passengers')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {tCommon('status')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-right text-xs font-medium uppercase tracking-wide">
                        {t('amount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pkgBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <Link
                            to={`/travel/bookings/${booking.id}`}
                            className="text-primary font-medium hover:underline"
                          >
                            {booking.bookingReference}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="bg-primary/10 flex h-8 w-8 items-center justify-center">
                              <User className="text-primary h-4 w-4" />
                            </div>
                            <div>
                              <p className="font-medium">
                                {booking.customer?.firstName} {booking.customer?.lastName}
                              </p>
                              <p className="text-muted-foreground text-xs">
                                {booking.customer?.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm">{booking.package?.name || t('customBooking')}</p>
                          {booking.package?.code && (
                            <p className="text-muted-foreground text-xs">{booking.package.code}</p>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="text-muted-foreground h-4 w-4" />
                            <span>
                              {formatDate(booking.travelStartDate)} -{' '}
                              {formatDate(booking.travelEndDate)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Users className="text-muted-foreground h-4 w-4" />
                            <span>
                              {booking.totalAdults} {t('adults')}
                              {booking.totalChildren > 0 &&
                                `, ${booking.totalChildren} ${t('children')}`}
                              {booking.totalInfants > 0 &&
                                `, ${booking.totalInfants} ${t('infants')}`}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            const labelKey = packageStatusLabelKeys[booking.status];
                            return (
                              <span
                                className={`px-2 py-0.5 text-xs font-medium ${
                                  packageStatusColors[booking.status] || 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {labelKey ? t(labelKey) : booking.status}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-medium">{formatCurrency(booking.totalAmount)}</p>
                          {booking.balanceDue > 0 && (
                            <p className="text-xs text-orange-600">
                              {t('due')}: {formatCurrency(booking.balanceDue)}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pkgPagination.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    {t('showingResults', {
                      from: (pkgPagination.page - 1) * pkgPagination.limit + 1,
                      to: Math.min(pkgPagination.page * pkgPagination.limit, pkgPagination.total),
                      total: pkgPagination.total,
                    })}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPkgPage((p) => Math.max(1, p - 1))}
                      disabled={pkgPagination.page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {tCommon('previous')}
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, pkgPagination.totalPages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <Button
                            key={pageNum}
                            variant={pageNum === pkgPagination.page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setPkgPage(pageNum)}
                            className="w-8"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPkgPage((p) => Math.min(pkgPagination.totalPages, p + 1))}
                      disabled={pkgPagination.page >= pkgPagination.totalPages}
                    >
                      {tCommon('next')}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Hotel Bookings Tab */}
        <TabsContent value="hotels" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={hotelStatusFilter ?? ''}
              onChange={(e) => {
                setHotelStatusFilter((e.target.value as HotelBookingStatus) || undefined);
                setHotelPage(1);
              }}
              className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1"
            >
              <option value="">{t('allStatuses')}</option>
              <option value="PENDING">{t('status.pending')}</option>
              <option value="CONFIRMED">{t('status.confirmed')}</option>
              <option value="CANCELLED">{t('status.cancelled')}</option>
              <option value="REFUNDED">{t('status.refunded')}</option>
            </select>
          </div>

          {/* Loading State */}
          {hotelLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="text-primary h-8 w-8 animate-spin" />
              <span className="text-muted-foreground ml-2">{t('loadingBookings')}</span>
            </div>
          )}

          {/* Error State */}
          {hotelError && (
            <div className="border border-red-200 bg-red-50 p-4 text-red-700">
              <p className="font-medium">{t('errorLoading')}</p>
              <p className="text-sm">{hotelError.message}</p>
            </div>
          )}

          {/* Empty State */}
          {!hotelLoading && !hotelError && hotelBookings.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Building2 className="text-muted-foreground/50 h-12 w-12" />
              <h3 className="mt-4 text-lg font-medium">{t('noHotelBookingsFound')}</h3>
              <p className="text-muted-foreground mt-1 text-sm">
                {hotelStatusFilter ? t('adjustFilters') : t('hotelBookingsWillAppear')}
              </p>
            </div>
          )}

          {/* Hotel Bookings Table */}
          {!hotelLoading && !hotelError && hotelBookings.length > 0 && (
            <>
              <div className="border-border bg-card overflow-hidden border">
                <table className="w-full">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('reference')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('hotel')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('customer')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('stayDates')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('details')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {tCommon('status')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-right text-xs font-medium uppercase tracking-wide">
                        {t('amount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {hotelBookings.map((booking) => (
                      <tr key={booking.id} className="hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <Link
                            to={`/bookings/hotels/${booking.id}`}
                            className="text-primary font-medium hover:underline"
                          >
                            {booking.bookingItem?.booking?.bookingReference ||
                              booking.amadeusBookingId}
                          </Link>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="bg-primary/10 flex h-8 w-8 items-center justify-center">
                              <Building2 className="text-primary h-4 w-4" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{booking.hotelName}</p>
                              <p className="text-muted-foreground text-xs">
                                {booking.roomType || t('standardRoom')}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="bg-primary/10 flex h-8 w-8 items-center justify-center">
                              <User className="text-primary h-4 w-4" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">
                                {booking.bookingItem?.booking?.customer?.firstName}{' '}
                                {booking.bookingItem?.booking?.customer?.lastName}
                              </p>
                              <p className="text-muted-foreground text-xs">
                                {booking.bookingItem?.booking?.customer?.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="text-muted-foreground h-4 w-4" />
                            <span>
                              {formatDate(booking.checkInDate)} - {formatDate(booking.checkOutDate)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-sm">
                            <p>
                              {booking.roomCount} {t('rooms')}, {booking.guestCount} {t('guests')}
                            </p>
                            {booking.boardType && (
                              <p className="text-muted-foreground text-xs">
                                {booking.boardType.replace('_', ' ')}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            const labelKey = hotelStatusLabelKeys[booking.status];
                            return (
                              <span
                                className={`px-2 py-0.5 text-xs font-medium ${
                                  hotelStatusColors[booking.status] || 'bg-gray-100 text-gray-700'
                                }`}
                              >
                                {labelKey ? t(labelKey) : booking.status}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-medium">
                            {booking.currency} {Number(booking.sellingPrice).toFixed(2)}
                          </p>
                          <p className="text-muted-foreground text-xs">
                            {t('cost')}: {booking.currency}{' '}
                            {Number(booking.totalAmadeusPrice).toFixed(2)}
                          </p>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {hotelPagination.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    {t('showingResults', {
                      from: (hotelPagination.page - 1) * hotelPagination.limit + 1,
                      to: Math.min(
                        hotelPagination.page * hotelPagination.limit,
                        hotelPagination.total
                      ),
                      total: hotelPagination.total,
                    })}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setHotelPage((p) => Math.max(1, p - 1))}
                      disabled={hotelPagination.page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {tCommon('previous')}
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, hotelPagination.totalPages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <Button
                            key={pageNum}
                            variant={pageNum === hotelPagination.page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setHotelPage(pageNum)}
                            className="w-8"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setHotelPage((p) => Math.min(hotelPagination.totalPages, p + 1))
                      }
                      disabled={hotelPagination.page >= hotelPagination.totalPages}
                    >
                      {tCommon('next')}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* Flight Bookings Tab */}
        <TabsContent value="flights" className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={flightStatusFilter ?? ''}
              onChange={(e) => {
                setFlightStatusFilter((e.target.value as FlightBookingStatus) || undefined);
                setFlightPage(1);
              }}
              className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1"
            >
              <option value="">{t('allStatuses')}</option>
              <option value="PENDING">{t('status.pending')}</option>
              <option value="CONFIRMED">{t('status.confirmed')}</option>
              <option value="TICKETED">{t('status.ticketed')}</option>
              <option value="CANCELLED">{t('status.cancelled')}</option>
              <option value="REFUNDED">{t('status.refunded')}</option>
            </select>
          </div>

          {/* Loading State */}
          {flightLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="text-primary h-8 w-8 animate-spin" />
              <span className="text-muted-foreground ml-2">{t('loadingBookings')}</span>
            </div>
          )}

          {/* Error State */}
          {flightError && (
            <div className="border border-red-200 bg-red-50 p-4 text-red-700">
              <p className="font-medium">{t('errorLoading')}</p>
              <p className="text-sm">{flightError.message}</p>
            </div>
          )}

          {/* Empty State */}
          {!flightLoading && !flightError && flightBookings.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Plane className="text-muted-foreground/50 h-12 w-12" />
              <h3 className="mt-4 text-lg font-medium">{t('noFlightBookingsFound')}</h3>
              <p className="text-muted-foreground mt-1 text-sm">
                {flightStatusFilter ? t('adjustFilters') : t('flightBookingsWillAppear')}
              </p>
            </div>
          )}

          {/* Flight Bookings Table */}
          {!flightLoading && !flightError && flightBookings.length > 0 && (
            <>
              <div className="border-border bg-card overflow-hidden border">
                <table className="w-full">
                  <thead className="bg-muted/30">
                    <tr>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('reference')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('route')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('customer')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('flightDate')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {t('passengers')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-left text-xs font-medium uppercase tracking-wide">
                        {tCommon('status')}
                      </th>
                      <th className="text-muted-foreground px-4 py-2 text-right text-xs font-medium uppercase tracking-wide">
                        {t('amount')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {flightBookings.map((booking) => {
                      // Parse flight data to extract route info
                      const flightData = booking.flightData as {
                        itineraries?: Array<{
                          segments?: Array<{
                            departure?: { iataCode?: string; at?: string };
                            arrival?: { iataCode?: string };
                          }>;
                        }>;
                      } | null;
                      const firstItinerary = flightData?.itineraries?.[0];
                      const firstSegment = firstItinerary?.segments?.[0];
                      const lastSegment =
                        firstItinerary?.segments?.[firstItinerary.segments.length - 1];
                      const origin = firstSegment?.departure?.iataCode || '—';
                      const destination = lastSegment?.arrival?.iataCode || '—';
                      const departureDate = firstSegment?.departure?.at;

                      // Count passengers from passengerDetails
                      const passengers = booking.passengerDetails as Array<{
                        type?: string;
                      }> | null;
                      const passengerCount = passengers?.length || 0;

                      return (
                        <tr key={booking.id} className="hover:bg-muted/30 transition-colors">
                          <td className="px-4 py-3">
                            <Link
                              to={`/bookings/flights/${booking.id}`}
                              className="text-primary font-medium hover:underline"
                            >
                              {booking.bookingItem?.booking?.bookingReference ||
                                booking.amadeusOrderId}
                            </Link>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="bg-primary/10 flex h-8 w-8 items-center justify-center">
                                <Plane className="text-primary h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium">
                                  {origin} → {destination}
                                </p>
                                <p className="text-muted-foreground text-xs">
                                  {flightData?.itineraries?.length === 2
                                    ? t('roundTrip')
                                    : t('oneWay')}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="bg-primary/10 flex h-8 w-8 items-center justify-center">
                                <User className="text-primary h-4 w-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium">
                                  {booking.bookingItem?.booking?.customer?.firstName}{' '}
                                  {booking.bookingItem?.booking?.customer?.lastName}
                                </p>
                                <p className="text-muted-foreground text-xs">
                                  {booking.bookingItem?.booking?.customer?.email}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="text-muted-foreground h-4 w-4" />
                              <span>{departureDate ? formatDate(departureDate) : '—'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2 text-sm">
                              <Users className="text-muted-foreground h-4 w-4" />
                              <span>
                                {passengerCount} {t('pax')}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {(() => {
                              const labelKey = flightStatusLabelKeys[booking.status];
                              return (
                                <span
                                  className={`px-2 py-0.5 text-xs font-medium ${
                                    flightStatusColors[booking.status] ||
                                    'bg-gray-100 text-gray-700'
                                  }`}
                                >
                                  {labelKey ? t(labelKey) : booking.status}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <p className="font-medium">
                              {booking.currency} {Number(booking.sellingPrice).toFixed(2)}
                            </p>
                            <p className="text-muted-foreground text-xs">
                              {t('cost')}: {booking.currency}{' '}
                              {Number(booking.totalAmadeusPrice).toFixed(2)}
                            </p>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {flightPagination.totalPages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    {t('showingResults', {
                      from: (flightPagination.page - 1) * flightPagination.limit + 1,
                      to: Math.min(
                        flightPagination.page * flightPagination.limit,
                        flightPagination.total
                      ),
                      total: flightPagination.total,
                    })}
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setFlightPage((p) => Math.max(1, p - 1))}
                      disabled={flightPagination.page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {tCommon('previous')}
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, flightPagination.totalPages) }, (_, i) => {
                        const pageNum = i + 1;
                        return (
                          <Button
                            key={pageNum}
                            variant={pageNum === flightPagination.page ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setFlightPage(pageNum)}
                            className="w-8"
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFlightPage((p) => Math.min(flightPagination.totalPages, p + 1))
                      }
                      disabled={flightPagination.page >= flightPagination.totalPages}
                    >
                      {tCommon('next')}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Booking Modal */}
      {showBookingModal && (
        <BookingModal
          defaultDirection={parseDirection(direction ?? null)}
          open={showBookingModal}
          onOpenChange={setShowBookingModal}
          onSuccess={(bookingId) => navigate(`/travel/bookings/${bookingId}`)}
        />
      )}
    </div>
  );
}
